/**
 * Component: WidgetConfigPage
 * SpecReferenceID: ADMIN-V1
 * Requirements: ADMIN-V1-R16, CONFIG-V1-R18, WIDGET-V1-R28
 *
 * Admin page for the WIDGET-V1 runtime configuration.
 *
 * Loads the active widget config from
 * `GET /v1/config/widget`, lets an authorised admin edit
 * the config fields (chat assistant name, landing content,
 * enabled surfaces, theme tokens, allowed embed origins,
 * deployment settings), and posts the whole blob back to
 * `PUT /v1/config/widget`. The `Reset to defaults` button
 * calls `POST /v1/config/widget/reset` to drop back to the
 * safe-default state (no surfaces enabled, empty theme,
 * no allowed origins, assistant name "Galen", no landing
 * content).
 *
 * The sections are:
 *   1. Chat assistant name — the display name the Chat
 *      surface shows for the assistant ("Galen" by
 *      default; per-deployment for white-labelling).
 *   2. Landing content — optional HTML the widget renders
 *      (sanitised) above the mounted surface; a
 *      per-deployment intro blurb.
 *   2a. Browse contents depth (WIDGET-V1-R28) — how many
 *      section-hierarchy levels the Browse widget TOC
 *      lists, 1-5 (default 2).
 *   3. Enabled surfaces — checkbox group over the five
 *      canonical WIDGET-V1 surfaces (chat, galen-search,
 *      semantic-search, simple-search, browse). Empty
 *      selection means the widget renders nothing.
 *   4. Theme tokens — key/value editor for CSS custom
 *      properties applied to the widget root element.
 *      Free-form schema because deployments override
 *      different subsets.
 *   5. Allowed origins — string list editor for host
 *      domains permitted to embed the widget. Displayed
 *      for admin reference; actual enforcement happens
 *      via CORS at the gateway.
 *   6. Deployment settings — raw JSON textarea for the
 *      escape-hatch object. Validated client-side (must
 *      parse) before submit.
 *
 * Accessibility: WCAG 2.1 AA compliant (label associations,
 * keyboard-accessible add/remove buttons, accessible name
 * for icon-only buttons).
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  Loader2,
  Save,
  RotateCcw,
  Plus,
  Trash2,
  Settings,
  AlertCircle,
  Check,
} from 'lucide-react';

import api, { getErrorMessage } from '@/lib/api';
import {
  BROWSE_TOC_DEPTH_MAX,
  BROWSE_TOC_DEPTH_MIN,
  DEFAULT_BROWSE_TOC_DEPTH,
  DEFAULT_WIDGET_CONFIG,
  WIDGET_SURFACES,
  type WidgetConfig,
  type WidgetConfigApiResponse,
  type WidgetConfigUpdateRequest,
  type WidgetSurface,
} from '@/types/widget';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';

// Human-readable labels for the five canonical surfaces.
// Pinned alongside WIDGET_SURFACES so the form order and
// labels stay deterministic.
const SURFACE_LABELS: Record<WidgetSurface, string> = {
  'chat': 'Chat',
  'galen-search': 'Galen Search',
  'semantic-search': 'Semantic Search',
  'simple-search': 'Simple Search',
  'browse': 'Browse (guideline browser)',
};

interface ThemeEntry {
  key: string;
  value: string;
}

/** AB#4401: fallback when the config blob omits assistant_name. */
const DEFAULT_ASSISTANT_NAME = 'Galen';
const ASSISTANT_NAME_MAX_LEN = 64;
/** AB#4404: bound on the deployment landing-content HTML blob. */
const LANDING_CONTENT_MAX_LEN = 20000;

/** WIDGET-V1-R28: the selectable Browse TOC depths, 1..5. */
const BROWSE_TOC_DEPTH_OPTIONS = Array.from(
  { length: BROWSE_TOC_DEPTH_MAX - BROWSE_TOC_DEPTH_MIN + 1 },
  (_, i) => BROWSE_TOC_DEPTH_MIN + i,
);

/**
 * WIDGET-V1-R28: coerce a loaded depth to a value the select
 * can show and the backend will accept. A blob from a backend
 * that pre-dates the field (absent) or a malformed value falls
 * back to the default rather than bouncing the next save.
 */
function normaliseBrowseTocDepth(value: unknown): number {
  return typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= BROWSE_TOC_DEPTH_MIN &&
    value <= BROWSE_TOC_DEPTH_MAX
    ? value
    : DEFAULT_BROWSE_TOC_DEPTH;
}

interface FormState {
  assistantName: string;
  landingContent: string;
  browseTocDepth: number;
  enabledSurfaces: Set<WidgetSurface>;
  themeEntries: ThemeEntry[];
  allowedOrigins: string[];
  deploymentSettingsJson: string;
}

/**
 * Convert the API config blob into the editable form
 * state. Theme dict becomes an ordered array of {key, value}
 * pairs for stable React rendering, and deployment_settings
 * is serialised to a pretty-printed JSON string for the
 * raw textarea editor.
 */
function configToFormState(config: WidgetConfig): FormState {
  return {
    assistantName: (config.assistant_name ?? '').trim() || DEFAULT_ASSISTANT_NAME,
    landingContent: config.landing_content ?? '',
    browseTocDepth: normaliseBrowseTocDepth(config.browse_toc_depth),
    enabledSurfaces: new Set(config.enabled_surfaces),
    themeEntries: Object.entries(config.theme).map(([key, value]) => ({
      key,
      value,
    })),
    allowedOrigins: [...config.allowed_origins],
    deploymentSettingsJson: JSON.stringify(
      config.deployment_settings ?? {},
      null,
      2,
    ),
  };
}

/**
 * Convert form state back to the API config blob.
 *
 * Throws a descriptive Error if the deployment-settings
 * JSON is unparseable so the caller can show the message
 * inline rather than letting JSON.parse throw a generic
 * SyntaxError.
 */
function formStateToConfig(state: FormState): WidgetConfig {
  let deploymentSettings: Record<string, unknown> = {};
  const trimmed = state.deploymentSettingsJson.trim();
  if (trimmed.length > 0) {
    try {
      const parsed = JSON.parse(trimmed) as unknown;
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        throw new Error('Deployment settings must be a JSON object.');
      }
      deploymentSettings = parsed as Record<string, unknown>;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      throw new Error(`Deployment settings JSON is invalid: ${msg}`);
    }
  }

  // Theme entries: collapse duplicate keys (last write wins),
  // drop empty keys silently — they cannot survive in JSONB
  // and an admin who wants to "delete" a key uses the row
  // remove button.
  const theme: Record<string, string> = {};
  for (const entry of state.themeEntries) {
    const k = entry.key.trim();
    if (k.length === 0) continue;
    theme[k] = entry.value;
  }

  // Allowed origins: drop empty strings; keep ordering
  // because some admin tools care about list order even
  // though CORS does not.
  const allowedOrigins = state.allowedOrigins
    .map((o) => o.trim())
    .filter((o) => o.length > 0);

  // AB#4401: assistant name — trim, fall back to "Galen" when
  // blank, and clamp to the backend's 64-char bound so a save
  // can't bounce on a length validation error.
  const assistantName =
    state.assistantName.trim().slice(0, ASSISTANT_NAME_MAX_LEN) ||
    DEFAULT_ASSISTANT_NAME;

  return {
    assistant_name: assistantName,
    // AB#4404: clamp the landing-content HTML to the backend bound
    // (don't trim — leading whitespace in HTML is meaningful).
    landing_content: state.landingContent.slice(0, LANDING_CONTENT_MAX_LEN),
    // WIDGET-V1-R28: always send an in-range depth.
    browse_toc_depth: normaliseBrowseTocDepth(state.browseTocDepth),
    enabled_surfaces: WIDGET_SURFACES.filter((s) => state.enabledSurfaces.has(s)),
    theme,
    allowed_origins: allowedOrigins,
    deployment_settings: deploymentSettings,
  };
}

export const WidgetConfigPage: React.FC = () => {
  const [formState, setFormState] = useState<FormState>(() =>
    configToFormState(DEFAULT_WIDGET_CONFIG),
  );
  const [meta, setMeta] = useState<{
    version: number;
    updatedAt: string;
    updatedBy: string;
  } | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [resetting, setResetting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<WidgetConfigApiResponse>('/config/widget');
      setFormState(configToFormState(res.data.config));
      setMeta({
        version: res.data.version,
        updatedAt: res.data.updated_at,
        updatedBy: res.data.updated_by,
      });
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  const handleAssistantNameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setFormState((prev) => ({ ...prev, assistantName: value }));
    },
    [],
  );

  const handleLandingContentChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = event.target.value;
      setFormState((prev) => ({ ...prev, landingContent: value }));
    },
    [],
  );

  const handleBrowseTocDepthChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const value = Number(event.target.value);
      setFormState((prev) => ({ ...prev, browseTocDepth: value }));
    },
    [],
  );

  const handleSurfaceToggle = useCallback(
    (surface: WidgetSurface, checked: boolean) => {
      setFormState((prev) => {
        const next = new Set(prev.enabledSurfaces);
        if (checked) {
          next.add(surface);
        } else {
          next.delete(surface);
        }
        return { ...prev, enabledSurfaces: next };
      });
    },
    [],
  );

  const handleThemeAdd = useCallback(() => {
    setFormState((prev) => ({
      ...prev,
      themeEntries: [...prev.themeEntries, { key: '', value: '' }],
    }));
  }, []);

  const handleThemeRemove = useCallback((index: number) => {
    setFormState((prev) => ({
      ...prev,
      themeEntries: prev.themeEntries.filter((_, i) => i !== index),
    }));
  }, []);

  const handleThemeChange = useCallback(
    (index: number, field: 'key' | 'value', value: string) => {
      setFormState((prev) => ({
        ...prev,
        themeEntries: prev.themeEntries.map((entry, i) =>
          i === index ? { ...entry, [field]: value } : entry,
        ),
      }));
    },
    [],
  );

  const handleOriginAdd = useCallback(() => {
    setFormState((prev) => ({
      ...prev,
      allowedOrigins: [...prev.allowedOrigins, ''],
    }));
  }, []);

  const handleOriginRemove = useCallback((index: number) => {
    setFormState((prev) => ({
      ...prev,
      allowedOrigins: prev.allowedOrigins.filter((_, i) => i !== index),
    }));
  }, []);

  const handleOriginChange = useCallback((index: number, value: string) => {
    setFormState((prev) => ({
      ...prev,
      allowedOrigins: prev.allowedOrigins.map((o, i) => (i === index ? value : o)),
    }));
  }, []);

  const handleDeploymentSettingsChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setFormState((prev) => ({
        ...prev,
        deploymentSettingsJson: event.target.value,
      }));
    },
    [],
  );

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    let config: WidgetConfig;
    try {
      config = formStateToConfig(formState);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSaving(false);
      return;
    }

    try {
      const payload: WidgetConfigUpdateRequest = { config };
      const res = await api.put<WidgetConfigApiResponse>('/config/widget', payload);
      setFormState(configToFormState(res.data.config));
      setMeta({
        version: res.data.version,
        updatedAt: res.data.updated_at,
        updatedBy: res.data.updated_by,
      });
      setSuccessMessage(`Widget configuration saved (version ${res.data.version}).`);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }, [formState]);

  const handleReset = useCallback(async () => {
    setResetting(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const res = await api.post<WidgetConfigApiResponse>('/config/widget/reset');
      setFormState(configToFormState(res.data.config));
      setMeta({
        version: res.data.version,
        updatedAt: res.data.updated_at,
        updatedBy: res.data.updated_by,
      });
      setSuccessMessage(
        `Widget configuration reset to defaults (version ${res.data.version}).`,
      );
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setResetting(false);
    }
  }, []);

  if (loading) {
    return (
      <div className="container max-w-4xl mx-auto py-8">
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <span className="ml-3 text-muted-foreground">Loading widget configuration…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container max-w-4xl mx-auto py-8 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Settings className="h-7 w-7" aria-hidden="true" />
            Widget Configuration
          </h1>
          <p className="text-muted-foreground mt-2">
            Configure the WIDGET-V1 runtime: the chat assistant name, the landing
            content shown above the widget, which surfaces are enabled, the theme
            applied to embedded widgets, the host origins permitted to embed the
            widget, and any deployment-specific overrides.
          </p>
          {meta && (
            <p className="text-sm text-muted-foreground mt-2">
              Current version: <strong>{meta.version}</strong> · last updated by{' '}
              <strong>{meta.updatedBy}</strong> at{' '}
              <time dateTime={meta.updatedAt}>{new Date(meta.updatedAt).toLocaleString()}</time>
            </p>
          )}
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {successMessage && (
        <Alert>
          <Check className="h-4 w-4" />
          <AlertDescription>{successMessage}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Chat assistant name</CardTitle>
          <CardDescription>
            The display name shown for the assistant in the Chat surface (next to
            the bot avatar). Per-deployment so a customer can white-label the chat.
            Leave blank to use the default, &quot;Galen&quot;. Maximum{' '}
            {ASSISTANT_NAME_MAX_LEN} characters.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Input
            aria-label="Assistant name"
            value={formState.assistantName}
            onChange={handleAssistantNameChange}
            maxLength={ASSISTANT_NAME_MAX_LEN}
            placeholder="Galen"
            className="max-w-sm"
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Landing content</CardTitle>
          <CardDescription>
            Optional HTML shown at the top of the embedded widget (above the
            mounted surface) — a per-deployment intro / &quot;what this guideline
            set is&quot; blurb. The same allow-list as guideline content applies
            (headings, paragraphs, lists, links, basic emphasis; scripts and
            styles are stripped). Leave blank for no blurb. Maximum{' '}
            {LANDING_CONTENT_MAX_LEN.toLocaleString()} characters.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            aria-label="Landing content (HTML)"
            value={formState.landingContent}
            onChange={handleLandingContentChange}
            maxLength={LANDING_CONTENT_MAX_LEN}
            rows={8}
            className="font-mono text-sm"
            placeholder="<h2>About these guidelines</h2>&#10;<p>…</p>"
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Browse contents depth</CardTitle>
          <CardDescription>
            How many levels of a guideline&apos;s section hierarchy the Browse
            widget&apos;s table of contents lists. Level 1 is the top-level
            sections; deeper levels are their sub-sections. Sections below the
            chosen depth still appear in the guideline text. Default{' '}
            {DEFAULT_BROWSE_TOC_DEPTH}.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Label htmlFor="browse-toc-depth" className="sr-only">
            Browse contents depth
          </Label>
          <select
            id="browse-toc-depth"
            value={formState.browseTocDepth}
            onChange={handleBrowseTocDepthChange}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm max-w-sm"
          >
            {BROWSE_TOC_DEPTH_OPTIONS.map((depth) => (
              <option key={depth} value={depth}>
                {depth === 1 ? '1 level' : `${depth} levels`}
                {depth === DEFAULT_BROWSE_TOC_DEPTH ? ' (default)' : ''}
              </option>
            ))}
          </select>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Enabled surfaces</CardTitle>
          <CardDescription>
            Which of the five WIDGET-V1 surfaces this deployment exposes via the
            widget. Surfaces left unchecked are not rendered even if the host page
            mounts them.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {WIDGET_SURFACES.map((surface) => (
            <div key={surface} className="flex items-center gap-3">
              <input
                type="checkbox"
                id={`surface-${surface}`}
                checked={formState.enabledSurfaces.has(surface)}
                onChange={(e) => handleSurfaceToggle(surface, e.target.checked)}
                className="h-4 w-4"
              />
              <Label htmlFor={`surface-${surface}`} className="cursor-pointer">
                {SURFACE_LABELS[surface]}
                <span className="ml-2 text-xs text-muted-foreground font-mono">
                  {surface}
                </span>
              </Label>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Theme tokens</CardTitle>
          <CardDescription>
            CSS custom property overrides applied to the widget root element. Keys
            are CSS variable names (e.g. <code>primary</code>, <code>accent</code>,
            <code>font-family</code>); values are CSS-valid strings. Leave empty to
            use the widget&apos;s built-in neutral theme.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {formState.themeEntries.length === 0 && (
            <p className="text-sm text-muted-foreground italic">No theme overrides set.</p>
          )}
          {formState.themeEntries.map((entry, index) => (
            <div key={index} className="flex items-center gap-2">
              <Input
                aria-label={`Theme key ${index + 1}`}
                placeholder="key (e.g. primary)"
                value={entry.key}
                onChange={(e) => handleThemeChange(index, 'key', e.target.value)}
                className="flex-1"
              />
              <Input
                aria-label={`Theme value ${index + 1}`}
                placeholder="value (e.g. #22c55e)"
                value={entry.value}
                onChange={(e) => handleThemeChange(index, 'value', e.target.value)}
                className="flex-1"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Remove theme entry ${index + 1}`}
                onClick={() => handleThemeRemove(index)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={handleThemeAdd}>
            <Plus className="h-4 w-4 mr-2" />
            Add theme override
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Allowed origins</CardTitle>
          <CardDescription>
            Host domains permitted to embed this deployment&apos;s widget. Displayed
            for admin reference; actual enforcement happens via CORS at the API
            gateway. An empty list means no host is allowed.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {formState.allowedOrigins.length === 0 && (
            <p className="text-sm text-muted-foreground italic">No allowed origins set.</p>
          )}
          {formState.allowedOrigins.map((origin, index) => (
            <div key={index} className="flex items-center gap-2">
              <Input
                aria-label={`Allowed origin ${index + 1}`}
                placeholder="https://embed.example.com"
                value={origin}
                onChange={(e) => handleOriginChange(index, e.target.value)}
                className="flex-1"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Remove allowed origin ${index + 1}`}
                onClick={() => handleOriginRemove(index)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={handleOriginAdd}>
            <Plus className="h-4 w-4 mr-2" />
            Add allowed origin
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Deployment settings (advanced)</CardTitle>
          <CardDescription>
            Optional deployment-specific knobs — default landing surface, welcome
            message, safety validator UI toggles, etc. Free-form JSON object.
            Stable fields may be promoted to dedicated sections in future
            iterations.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            aria-label="Deployment settings JSON"
            value={formState.deploymentSettingsJson}
            onChange={handleDeploymentSettingsChange}
            rows={8}
            className="font-mono text-sm"
            placeholder="{}"
          />
        </CardContent>
      </Card>

      <div className="flex items-center gap-3">
        <Button onClick={() => void handleSave()} disabled={saving || resetting}>
          {saving ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Save changes
        </Button>
        <Button
          variant="outline"
          onClick={() => void handleReset()}
          disabled={saving || resetting}
        >
          {resetting ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <RotateCcw className="h-4 w-4 mr-2" />
          )}
          Reset to defaults
        </Button>
      </div>
    </div>
  );
};
